/**
 * Graphical Schedule Builder (Spec 201 — US2 / FR-007..013)
 *
 * The Schedules tab shows only the SAVED schedules list; building/editing happens in a modal
 * (opened by "Add new schedule" or a row's "Edit"). Compose a schedule visually (rules of
 * frequency/time/days/type) — never a raw cron string — with a live preview (plain-language
 * summary + next run times + validation) and first-class templates. Built from @/components/ui.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Clock } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Button,
  Badge,
  Input,
  Select,
  DataTable,
  EmptyState,
  LoadingState,
} from '@/components/ui';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';
import { useScheduleTemplates, useSchedulePreview } from '../../hooks/useScheduleBuilder';
import { formatEastern } from '@/utils/dateFormatter';
import { schedulesApi } from '../../api/schedules';
import type { ScheduleBuilder, ScheduleRule, Frequency, BackupType, ScheduleDefinition } from '../../api/schedules';
import { useAuth } from '../../hooks/useAuth';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const emptyRule = (): ScheduleRule => ({ backupType: 'full', frequency: 'daily', time: '02:00' });

export const GraphicalScheduleBuilder: React.FC = () => {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [builder, setBuilder] = useState<ScheduleBuilder>({
    timezone: 'America/New_York',
    rules: [emptyRule()],
  });
  const { data: templates } = useScheduleTemplates();
  const preview = useSchedulePreview(builder);
  const qc = useQueryClient();
  const saved = useQuery({ queryKey: ['backup', 'schedules'], queryFn: schedulesApi.list });
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']); // FR-031a: Admin+Operator may save schedules

  // Search + used/unused filter over the saved schedules (the list grows large once plans are seeded).
  const [q, setQ] = useState('');
  const [usedF, setUsedF] = useState('');
  const shown = useMemo(
    () =>
      (saved.data ?? []).filter(
        (s) =>
          (!usedF || (usedF === 'used' ? (s.usedByPlans ?? 0) > 0 : (s.usedByPlans ?? 0) === 0)) &&
          textMatch(q, s.name, s.summary)
      ),
    [saved.data, q, usedF]
  );

  const resetBuilder = () => {
    setEditingId(null);
    setName('');
    setBuilder({ timezone: 'America/New_York', rules: [emptyRule()] });
  };

  const openNew = () => {
    resetBuilder();
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetBuilder();
  };

  const loadForEdit = (s: ScheduleDefinition) => {
    try {
      const b = JSON.parse(s.builderJson) as ScheduleBuilder;
      setBuilder(b);
      setName(s.name);
      setEditingId(s.id);
      setModalOpen(true);
    } catch {
      /* bad builder json */
    }
  };

  const handleSave = async () => {
    if (!name) return;
    setSaving(true);
    try {
      if (editingId) await schedulesApi.update(editingId, { name, builder });
      else await schedulesApi.create(name, builder);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['backup', 'schedules'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'schedule-spread'] }),
      ]);
      setModalOpen(false);
      resetBuilder();
    } finally {
      setSaving(false);
    }
  };

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDelete = async (s: ScheduleDefinition) => {
    if ((s.usedByPlans ?? 0) > 0) return;
    if (!window.confirm(`Delete schedule "${s.name}"? This can't be undone.`)) return;
    // Spec 214: surface a 409/500 instead of an unhandled rejection (stale data can race).
    try {
      await schedulesApi.remove(s.id);
      setDeleteError(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
      return;
    }
    if (editingId === s.id) closeModal();
    await qc.invalidateQueries({ queryKey: ['backup', 'schedules'] });
  };

  const setRule = (i: number, patch: Partial<ScheduleRule>) =>
    setBuilder((b) => ({ ...b, rules: b.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRule = () => setBuilder((b) => ({ ...b, rules: [...b.rules, emptyRule()] }));
  const removeRule = (i: number) => setBuilder((b) => ({ ...b, rules: b.rules.filter((_, idx) => idx !== i) }));

  const toggleDay = (i: number, day: number) => {
    const r = builder.rules[i];
    const days = new Set(r.daysOfWeek ?? []);
    if (days.has(day)) days.delete(day);
    else days.add(day);
    setRule(i, { daysOfWeek: [...days].sort() });
  };

  return (
    <div className="space-y-6">
      {/* Saved schedules — the only thing on the tab. Build/edit happens in the modal. */}
      <Card>
        <CardHeader title="Saved schedules" subtitle="Reuse, edit, or delete a schedule. Delete is available only when no plan uses it." />
        <CardBody>
          {canManage && (
            <div className="mb-3 flex justify-end">
              <Button variant="primary" size="sm" onClick={openNew}>
                <Plus className="h-4 w-4" /> Add new schedule
              </Button>
            </div>
          )}
          {deleteError && (
            <p className="mb-3 rounded-md border border-[var(--color-status-danger)] px-3 py-2 text-sm text-[var(--color-status-danger)]">
              Delete failed: {deleteError}
            </p>
          )}
          {saved.isLoading ? (
            <LoadingState rows={3} />
          ) : (saved.data ?? []).length === 0 ? (
            <EmptyState
              title="No saved schedules yet"
              message={canManage ? 'Add one with the button above.' : 'No schedules have been created yet.'}
            />
          ) : (
            <>
              <ListFilterBar query={q} onQuery={setQ} placeholder="Search schedules…" count={shown.length} total={(saved.data ?? []).length}>
                <Select
                  value={usedF}
                  onChange={(e) => setUsedF(e.target.value)}
                  options={[
                    { value: '', label: 'All schedules' },
                    { value: 'used', label: 'In use' },
                    { value: 'unused', label: 'Unused' },
                  ]}
                />
              </ListFilterBar>
              <DataTable<ScheduleDefinition>
                minWidth="44rem"
                columns={[
                  {
                    key: 'name',
                    header: 'Schedule',
                    width: '22rem',
                    sortable: true,
                    render: (s) => (
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
                        <span className="font-medium text-[var(--color-text-primary)]">{s.name}</span>
                      </span>
                    ),
                  },
                  { key: 'summary', header: 'Cadence', wrap: true, sortable: false, render: (s) => s.summary || '—' },
                  {
                    key: 'usedByPlans',
                    header: 'Used by',
                    width: '8rem',
                    sortable: true,
                    // Spec 214 R13b: counts ACTIVE plans (what blocks delete + drives the
                    // materialized rows); archived usage shown muted, never a delete blocker.
                    render: (s) => (
                      <span className="inline-flex items-center gap-1.5">
                        {(s.usedByPlans ?? 0) > 0 ? (
                          <Badge variant="default">{s.usedByPlans} active plan{s.usedByPlans === 1 ? '' : 's'}</Badge>
                        ) : (
                          <span className="text-xs text-[var(--color-text-secondary)]">unused</span>
                        )}
                        {(s.usedByArchivedPlans ?? 0) > 0 && (
                          <span className="text-xs text-[var(--color-text-secondary)]">+{s.usedByArchivedPlans} archived</span>
                        )}
                      </span>
                    ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    width: '12rem',
                    sortable: false,
                    render: (s) =>
                      canManage ? (
                        <span className="flex justify-end gap-1.5">
                          <Button variant="outline" size="sm" onClick={() => loadForEdit(s)}>
                            <Pencil className="h-4 w-4" /> Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(s)}
                            disabled={(s.usedByPlans ?? 0) > 0}
                            title={(s.usedByPlans ?? 0) > 0 ? 'In use by a plan — detach it first' : 'Delete schedule'}
                          >
                            <Trash2 className="h-4 w-4" /> Delete
                          </Button>
                        </span>
                      ) : null,
                  },
                ]}
                rows={shown}
                rowKey={(s) => s.id}
              />
            </>
          )}
        </CardBody>
      </Card>

      {/* Build / edit a schedule — modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? `Edit schedule${name ? ` — ${name}` : ''}` : 'Add new schedule'}
        size="xl"
        footer={
          canManage ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button variant="primary" disabled={!name || saving || !preview.data?.valid} onClick={handleSave}>
                {saving ? 'Saving…' : editingId ? 'Update schedule' : 'Save schedule'}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Compose a schedule visually (e.g. weekly full + daily incremental) — no cron required. The preview shows the next runs.
          </p>

          {/* Templates */}
          {templates && templates.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--color-text-secondary)]">Templates:</span>
              {templates.map((t) => (
                <Button key={t.key} variant="outline" size="sm" onClick={() => setBuilder(t.builder)}>
                  {t.label}
                </Button>
              ))}
            </div>
          )}

          {/* Name + timezone */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Schedule name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Timezone (IANA)" value={builder.timezone} onChange={(e) => setBuilder({ ...builder, timezone: e.target.value })} />
          </div>

          {/* Rules */}
          <div className="space-y-3">
            {builder.rules.map((r, i) => (
              <div key={i} className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Select
                    label="Backup type"
                    value={r.backupType}
                    onChange={(e) => setRule(i, { backupType: e.target.value as BackupType })}
                    options={[
                      { value: 'full', label: 'Full' },
                      { value: 'incremental', label: 'Incremental' },
                    ]}
                  />
                  <Select
                    label="Frequency"
                    value={r.frequency}
                    onChange={(e) => setRule(i, { frequency: e.target.value as Frequency })}
                    options={[
                      { value: 'hourly', label: 'Hourly' },
                      { value: 'daily', label: 'Daily' },
                      { value: 'weekly', label: 'Weekly' },
                      { value: 'monthly', label: 'Monthly' },
                    ]}
                  />
                  {r.frequency === 'hourly' ? (
                    <Input label="Every N hours" type="number" value={String(r.intervalHours ?? 1)} onChange={(e) => setRule(i, { intervalHours: Number(e.target.value) })} />
                  ) : (
                    <Input label="Time" type="time" value={r.time ?? '02:00'} onChange={(e) => setRule(i, { time: e.target.value })} />
                  )}
                  {r.frequency === 'monthly' && (
                    <Input label="Day of month" type="number" value={String(r.dayOfMonth ?? 1)} onChange={(e) => setRule(i, { dayOfMonth: Number(e.target.value) })} />
                  )}
                </div>
                {r.backupType === 'incremental' && (
                  <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                    <strong>Incremental applies to lakehouses only</strong> (a Delta version diff of changed files).
                    Warehouses have no change‑tracking, so a warehouse target always runs a <strong>full</strong> export —
                    an incremental rule assigned to a warehouse runs as full.
                  </p>
                )}
                {r.frequency === 'weekly' && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {DOW.map((d, idx) => (
                      <Button
                        key={d}
                        variant={(r.daysOfWeek ?? []).includes(idx) ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => toggleDay(i, idx)}
                      >
                        {d}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => removeRule(i)} disabled={builder.rules.length <= 1}>
                    <Trash2 className="h-4 w-4" /> Remove rule
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <Button variant="outline" size="sm" onClick={addRule}>
              <Plus className="h-4 w-4" /> Add rule
            </Button>
          </div>

          {/* Live preview */}
          <div className="rounded-md border border-[var(--color-border)] p-3">
            <p className="mb-2 text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">Preview</p>
            {!preview.data ? (
              <p className="text-sm text-[var(--color-text-secondary)]">Add a rule to preview.</p>
            ) : !preview.data.valid ? (
              <div className="space-y-1">
                {preview.data.errors.map((err, i) => (
                  <p key={i} className="text-sm text-[var(--color-status-danger)]">{err}</p>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{preview.data.summary}</p>
                {/* Spec 214 T065: first run PER cadence — a weekly full next to an hourly
                    incremental never appeared in the merged next-5 list. */}
                {(preview.data.nextPerType ?? []).length > 1 && (
                  <p className="text-xs text-[var(--color-text-secondary)]">
                    {(preview.data.nextPerType ?? [])
                      .map((r) => `next ${r.backupType}: ${formatEastern(r.time)}`)
                      .join(' · ')}
                  </p>
                )}
                <ul className="space-y-1">
                  {(preview.data.nextRuns ?? []).map((nr, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Badge variant={nr.backupType === 'full' ? 'info' : 'default'}>{nr.backupType}</Badge>
                      <span>{formatEastern(nr.time)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GraphicalScheduleBuilder;
